import { useState } from "react";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { useUpdateVersionMetadata } from "../../../../api/versionHistory/versionHistory";
import { VERSION_DETAILS_COPY } from "../constants/versionDetails.constants";
import { buildVersionDetailsPayload } from "../helpers/versionDetails.helpers";
import { VersionDetailsDraft, VersionHistoryGroup } from "../types/versionHistory.types";

/**
 * Owns *which* version is being edited and the save mutation; the draft values themselves live
 * in the row's own form. Panel-level, matching `useVersionRename`: it gives mutual exclusion
 * (one open editor at a time) for free, and puts refetch/onSaved where the panel can reach them.
 */
export function useVersionDetailsEditor(params: { onSaved: () => void; refetchVersions: () => void }) {
  /**
   * The version as it was when editing began, not a live reference. A background refetch
   * (keepPreviousData) can replace the list mid-edit; holding the snapshot keeps the diff
   * baseline stable so a save still means "what the user actually changed".
   */
  const [editingVersion, setEditingVersion] = useState<VersionHistoryGroup | null>(null);
  const [versionToDelete, setVersionToDelete] = useState<VersionHistoryGroup | null>(null);
  const { mutateAsync, isLoading } = useUpdateVersionMetadata();

  const startEdit = (version: VersionHistoryGroup) => {
    // The legacy bucket has no version to write to.
    if (!version.versionId) return;
    setEditingVersion(version);
  };

  const cancel = () => setEditingVersion(null);

  const requestDelete = (version: VersionHistoryGroup) => {
    // The legacy bucket has no version to write to.
    if (!version.versionId) return;
    setVersionToDelete(version);
  };

  const cancelDelete = () => setVersionToDelete(null);

  /** Clears all three fields at once, regardless of which ones are actually populated. */
  const confirmDelete = async () => {
    if (!versionToDelete?.versionId) return;

    try {
      await mutateAsync({
        versionId: versionToDelete.versionId,
        reportingYear: null,
        numberOfUnits: null,
        pricePerUnit: null
      });
      successMessage(VERSION_DETAILS_COPY.deleteSuccess);
      setVersionToDelete(null);
      // The open editor's draft was seeded from the now-stale version -- close it too.
      setEditingVersion(current => (current?.versionId === versionToDelete.versionId ? null : current));
      params.refetchVersions();
      params.onSaved();
    } catch {
      // The axios interceptor already toasts the server's message for every failed request.
      setVersionToDelete(null);
    }
  };

  /** Returns an error message for the form to show, or null on success (including a no-op). */
  const commit = async (draft: VersionDetailsDraft): Promise<string | null> => {
    if (!editingVersion) return null;

    const payload = buildVersionDetailsPayload({ version: editingVersion, draft });
    if (!payload) {
      cancel();
      return null;
    }

    try {
      await mutateAsync(payload);
      cancel();
      successMessage(VERSION_DETAILS_COPY.saveSuccess);
      params.refetchVersions();
      params.onSaved();
      return null;
    } catch (err: any) {
      return err?.response?.data?.message || VERSION_DETAILS_COPY.saveError;
    }
  };

  return {
    editingVersionId: editingVersion?.versionId ?? null,
    isSaving: isLoading,
    startEdit,
    cancel,
    commit,
    versionToDelete,
    isDeleting: isLoading,
    requestDelete,
    cancelDelete,
    confirmDelete
  };
}
